/**
 * Gateway configuration — single source of truth for every route the frontend calls.
 *
 * All traffic goes through the YARP gateway (:5050). The gateway owns identity
 * headers: it derives X-Tenant-Id / X-User-Id / X-User-Roles from the JWT and
 * STRIPS any client-sent copies — the client only ever sends
 * `Authorization: Bearer <token>` (+ X-Correlation-Id, which the gateway echoes).
 */

declare global {
  interface ImportMeta {
    env?: Record<string, string | undefined>;
  }
}

function resolveBaseUrl(): string {
  // Vite (admin-dashboard) — checked with !== undefined (not truthiness) so an
  // intentionally empty string (same-origin, routed through the Vite dev proxy)
  // doesn't fall through to the hardcoded default below. ROUTES paths already
  // include the full '/api/v1/...' prefix, so this must be just the origin/host
  // (or '' for same-origin) — never another '/api' segment, or requests double up
  // to '/api/api/v1/...' and never reach the gateway's real routes.
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL !== undefined) {
    return import.meta.env.VITE_API_URL;
  }
  // Next.js (cleaning-app) — inlined at build time
  if (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_API_URL) {
    return process.env.NEXT_PUBLIC_API_URL;
  }
  return 'http://localhost:5050';
}

export const API_BASE_URL = resolveBaseUrl();
export const REQUEST_TIMEOUT_MS = 15_000;

/** Route table mirroring the gateway's ReverseProxy config (Backend-System/API-Engine). */
export const ROUTES = {
  auth: {
    login: '/api/v1/auth/login',
    register: '/api/v1/auth/register',
    refresh: '/api/v1/auth/refresh',
    forgotPassword: '/api/v1/auth/forgot-password',
    resetPassword: '/api/v1/auth/reset-password',
    verifyEmail: '/api/v1/auth/verify-email',
    roles: '/api/v1/auth/roles',
    users: '/api/v1/auth/users', // admin-only (gateway route-role map)
    userCount: '/api/v1/auth/users/count', // manager+ — staff-seat count only, no PII
    google: '/api/v1/auth/google', // anonymous — exchanges a real Google ID token
    guest: '/api/v1/auth/guest', // anonymous — creates a passwordless guest-checkout account
    me: '/api/v1/auth/me',
    inviteStoreOwner: '/api/v1/auth/invite-store-owner', // platform admin only
    acceptStoreInvite: '/api/v1/auth/accept-store-invite', // anonymous — the token is the credential
  },
  quotes: '/api/v1/quotes',
  bookings: '/api/v1/bookings',
  jobs: '/api/v1/jobs',
  // Distinct from `jobs` above (that's the scheduled-visit entity) — "Post a Job" customer
  // postings, matched against workers by area+skill. customer|manager (gateway route-role map).
  jobPostings: '/api/v1/job-postings',
  catalog: {
    serviceAreas: '/api/v1/catalog/service-areas',
    packages: '/api/v1/catalog/packages',
    items: '/api/v1/catalog/items',
    roomTypes: '/api/v1/catalog/room-types',
  },
  catalogAdmin: '/api/v1/catalog-admin', // manager+
  campaigns: '/api/v1/campaigns', // manager+
  blog: {
    base: '/api/v1/blog', // manager+ list/get/create/update/delete
    public: '/api/v1/blog/public',
    categories: '/api/v1/blog/categories', // anonymous
  },
  schedule: '/api/v1/schedule', // manager+
  customers: '/api/v1/customers',
  cms: {
    sections: '/api/v1/cms/sections', // manager+
    public: (page: string) => `/api/v1/cms/public/${encodeURIComponent(page)}`,
  },
  testimonials: {
    base: '/api/v1/testimonials', // manager+ list/create/update/delete
    public: '/api/v1/testimonials/public',
  },
  subscriptions: {
    plans: '/api/v1/subscriptions/plans', // manager+ read; admin write
    tenant: '/api/v1/subscriptions/tenant', // manager+, own tenant
    tenantUpgrade: '/api/v1/subscriptions/tenant/upgrade',
    tenantCancel: '/api/v1/subscriptions/tenant/cancel',
    tenantCheckout: '/api/v1/subscriptions/tenant/checkout', // real Stripe Checkout Session
    tenantPortal: '/api/v1/subscriptions/tenant/portal', // real Stripe Customer Portal
    allTenants: '/api/v1/subscriptions/tenants', // admin only
    dashboard: '/api/v1/subscriptions/dashboard', // admin only
    tenantsExportCsv: '/api/v1/subscriptions/admin/tenants/export.csv', // admin only
    tenantDiscount: (tenantId: string) => `/api/v1/subscriptions/admin/tenants/${tenantId}/discount`, // admin only
    tenantRefund: (tenantId: string) => `/api/v1/subscriptions/admin/tenants/${tenantId}/refund`, // admin only
    tenantFeatures: (tenantId: string) => `/api/v1/subscriptions/admin/tenants/${tenantId}/features`, // admin only
    analyticsMrr: '/api/v1/subscriptions/admin/analytics/mrr', // admin only
    analyticsChurn: '/api/v1/subscriptions/admin/analytics/churn', // admin only
    analyticsDistribution: '/api/v1/subscriptions/admin/analytics/distribution', // admin only
    analyticsMrrExportCsv: '/api/v1/subscriptions/admin/analytics/mrr/export.csv', // admin only
  },
  tenants: {
    base: '/api/v1/tenants', // platform admin only (TenantsController) — the tenant registry itself
    deactivate: (id: string) => `/api/v1/tenants/${id}/deactivate`,
  },
  site: {
    base: '/api/v1/site', // manager+ get/upsert
    public: '/api/v1/site/public',
  },
  images: {
    base: '/api/v1/images', // POST (upload, manager+) / GET+DELETE {**fileName}
    // ImagesController.List uses [HttpGet("~/api/v1/images-admin")] — an absolute route
    // override, so this is NOT nested under `base` despite living in the same controller.
    admin: '/api/v1/images-admin', // manager+ list
  },
  contacts: '/api/v1/contacts', // GET/PUT/DELETE manager+; POST /public is anonymous
  messages: '/api/v1/messages', // any authenticated role (no gateway route-role entry)
  workers: '/api/v1/workers', // worker|manager
  checkins: '/api/v1/checkins', // worker|manager
  media: '/api/v1/media',
  invoices: '/api/v1/invoices',
  payments: '/api/v1/payments', // manager+
  paymentIntent: '/api/v1/payments/create-intent', // customer,manager
  paymentRefund: '/api/v1/payments/refund', // manager+
  accounting: '/api/v1/accounting', // manager+
  reports: {
    generate: '/api/v1/reports/generate', // manager+
    list: '/api/v1/reports', // manager+
    get: (id: string) => `/api/v1/reports/${id}`, // manager+
    download: (id: string) => `/api/v1/reports/${id}/download`, // manager+
    delete: (id: string) => `/api/v1/reports/${id}`, // manager+
    schedules: '/api/v1/reports/schedules', // manager+
    schedule: (id: string) => `/api/v1/reports/schedules/${id}`, // manager+
  },
  products: '/api/v1/products', // anonymous reads; manager+ writes (in-service gated)
  categories: '/api/v1/categories', // anonymous reads; manager+ writes (in-service gated)
  heroSlides: '/api/v1/hero-slides', // anonymous reads; manager+ writes (in-service gated)
  promoTiles: '/api/v1/promo-tiles', // anonymous reads; manager+ writes (in-service gated)
  productCampaigns: '/api/v1/product-campaigns', // anonymous reads; manager+ writes (in-service gated)
  discounts: {
    rules: '/api/v1/discounts/rules', // manager+
    ruleAnalytics: (id: string) => `/api/v1/discounts/rules/${id}/analytics`, // manager+
    validate: (code: string) => `/api/v1/discounts/validate/${encodeURIComponent(code)}`, // anonymous
    calculate: '/api/v1/discounts/calculate', // customer,manager
  },
  locations: {
    base: '/api/v1/locations', // anonymous reads; manager+ writes
    copyTo: (id: string, targetId: string) => `/api/v1/locations/${id}/copy-to/${targetId}`, // manager+
    productPricing: (productId: string) => `/api/v1/products/${productId}/pricing`, // manager+
    bulkPricing: '/api/v1/products/bulk-pricing', // manager+
    countries: '/api/v1/locations/countries', // anonymous
    regions: '/api/v1/locations/regions', // anonymous
    suburbs: (locationId: string) => `/api/v1/locations/${locationId}/suburbs`, // anonymous reads; manager+ writes
  },
  // Native ProductService image/video upload for Products/HeroSlides/Campaigns — NOT
  // TenantService's `images` above. GET (serving) is anonymous; POST/DELETE are manager+
  // (in-service gated), same "anonymous prefix, controller-gated writes" shape as `products`.
  productMedia: '/api/v1/product-media',
  loyalty: '/api/v1/loyalty', // tiers anonymous; me/enroll customer+; tier CRUD + members manager+ (in-service gated)
  referrals: '/api/v1/referrals', // me/redeem customer+; stats manager+ (in-service gated)
  // Public reads/create live under /api/v1/products/{id}/reviews (see productsApi/reviewsApi
  // split below); this is only the manager-only moderation surface.
  productReviews: '/api/v1/product-reviews',
  wishlist: '/api/v1/wishlist', // customer+ (analytics/top narrowed to manager+ at the gateway)
  cart: '/api/v1/cart', // anonymous (guest) or customer
  orders: '/api/v1/orders', // customer,manager
  notifications: '/api/v1/notifications', // customer,manager (test-send + admin list are manager-only, gateway-narrowed)
  notificationTemplates: '/api/v1/notification-templates', // manager+
  opsOrderDlq: '/api/v1/ops/order/dlq', // admin only
  permissions: '/api/v1/permissions', // admin only
  roles: '/api/v1/roles', // admin only
  analytics: '/api/v1/analytics', // manager+
  ai: {
    chat: '/ai/chat',
    chatSeed: '/ai/chat/seed',
    chatSession: (id: string) => `/ai/chat/sessions/${encodeURIComponent(id)}`,
    recommendations: (productId: string, limit: number) =>
      `/ai/recommendations/${encodeURIComponent(productId)}?limit=${limit}`,
    // manager+ only (RouteAuthorization) — real per-feature Prometheus counters and a
    // Redis-backed audit log, see ai-engine/app/routers/verification.py.
    usageStats: '/ai/usage-stats',
    auditLogs: (limit = 50) => `/ai/audit-logs?limit=${limit}`,
    // Real tool-calling agents (LangGraph ReAct over ai-engine's own MCP server). agentChat is
    // anonymous (storefront shopping agent); the other two require a manager JWT — real
    // gateway-enforced RouteAuthorization, not just a frontend convention.
    agentChat: '/ai/agent/chat',
    analyticsAgentChat: '/ai/analytics-agent/chat',
    supportAgentChat: '/ai/support-agent/chat',
  },
  registry: '/registry',
  health: '/health',
  // TenantService/NewsletterController — subscribe/unsubscribe are anonymous "*"; list is
  // manager+ (gateway-narrowed from the wide-open TenantService base tier).
  newsletter: {
    subscribe: '/api/v1/newsletter/public',
    unsubscribe: (id: string) => `/api/v1/newsletter/public/unsubscribe/${encodeURIComponent(id)}`,
    list: (activeOnly = true) => `/api/v1/newsletter?activeOnly=${activeOnly}`,
  },
} as const;

/** @deprecated Back-compat shim for legacy SignalR/url helpers. Use API_BASE_URL. */
export const API_CONFIG = { baseURL: API_BASE_URL } as const;
